import { AnalyticIdentifier } from '@common/neo.tax';
import { ReactComponent as EmployeeIcon } from '@ui-components/neo.tax/dist/icons/employee.svg';
import { ReactComponent as DataSourcesIcon } from '@ui-components/neo.tax/dist/icons/link-accounting.svg';
import { ReactComponent as ProjectsIcon } from '@ui-components/neo.tax/dist/icons/presentation-projector-screen-hierarchy.svg';
import { ReactComponent as ExportIcon } from '@ui-components/neo.tax/dist/icons/receipt-1.svg';

import { getPath, NavSection, Path, PeriodName, SideNavItemProps, SideNavProps } from '@neo.tax/shared';

export const ascPaths = [
  Path.ASC_350_40,
  Path.ASC_PROJECTS,
  Path.ASC_WAGES,
  Path.ASC_DATA_SOURCES,
  Path.EXPORT_ASC,
] as const;

export type AscPath = (typeof ascPaths)[number];

interface AscPathItem {
  section: NavSection.ASC_350_40;
  path: Exclude<AscPath, Path.ASC_350_40>;
  title: string;
  icon: React.ReactElement;
  id: string;
  hideIfNonFinancialUser: boolean;
}

export const ASC_PATHS: AscPathItem[] = [
  {
    section: NavSection.ASC_350_40,
    path: Path.ASC_DATA_SOURCES,
    title: 'Data Sources',
    icon: <DataSourcesIcon />,
    id: `${AnalyticIdentifier.ENTERPRISE_NAV_ASC_DATA_SOURCES}`,
    hideIfNonFinancialUser: false,
  },
  {
    section: NavSection.ASC_350_40,
    path: Path.ASC_PROJECTS,
    title: 'Projects',
    icon: <ProjectsIcon />,
    id: `${AnalyticIdentifier.ENTERPRISE_NAV_ASC_PROJECTS}`,
    hideIfNonFinancialUser: false,
  },
  {
    section: NavSection.ASC_350_40,
    path: Path.ASC_WAGES,
    title: 'ASC 350-40 Cost',
    icon: <EmployeeIcon />,
    id: `${AnalyticIdentifier.ENTERPRISE_NAV_ASC_WAGES}`,
    hideIfNonFinancialUser: false,
  },
  {
    section: NavSection.ASC_350_40,
    path: Path.EXPORT_ASC,
    title: 'ASC 350-40 Documents',
    icon: <ExportIcon />,
    id: `${AnalyticIdentifier.ENTERPRISE_NAV_EXPORT_ASC}`,
    hideIfNonFinancialUser: false,
  },
];

export const isAscPathAvailable = ({
  isNonFinancialUser,
  path,
}: {
  isNonFinancialUser: boolean;
  path: AscPath;
}): boolean => {
  const { hideIfNonFinancialUser } = ASC_PATHS.find((p) => p.path === path) ?? {};

  if (hideIfNonFinancialUser && isNonFinancialUser) {
    return false;
  }

  return true;
};
export const getAscNavSection = ({
  companyId,
  fiscalYearId,
  isNonFinancialUser,
  periodName,
}: {
  isNonFinancialUser: boolean;
  companyId: string;
  fiscalYearId: string;
  periodName: PeriodName;
}): SideNavProps['sections'] => {
  const ascNavItems = ASC_PATHS.filter((ascPathItem) =>
    isAscPathAvailable({ isNonFinancialUser, path: ascPathItem.path }),
  ).reduce((acc: SideNavItemProps[], ascPathItem) => {
    const { icon, id, path, title } = ascPathItem;
    acc.push({
      id,
      title,
      icon,
      to: getPath({ path, companyId, fiscalYearId, periodName }),
    });
    return acc;
  }, []);

  return [{ title: NavSection.ASC_350_40, items: ascNavItems }];
};
